/**
 * My phone tabs (the bottom bar; Settings → Navigation): my order and hidden areas (contracts NavigationSettings),
 * saved with my account and cached on the device so the bar is right on the first frame and offline. The bar itself
 * comes from logic/navigation `phoneTabs` with each workspace's areas; the app's defaults ship in
 * packages/core/config/navigation.json.
 */
import type { NavigationSettings, PhoneArea, WorkspaceFeatures } from "@bundle/contracts";
import { type PhoneSection, type PhoneTabs, moveArea, parseNavigationSettings, phoneTabs, placeArea, setAreaHidden } from "../logic/navigation.ts";
import type { KeyValueStorage, SettingsApi } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";

export type NavigationState = {
  /** "cached": the device's copy, still checking with the server. */
  status: "loading" | "cached" | "ready" | "failed";
  settings: NavigationSettings;
  saving: boolean;
  /** The last save failed (the previous settings are back). */
  saveFailed: boolean;
};

export type NavigationDeps = {
  api: Pick<SettingsApi, "navigationSettings" | "updateNavigationSettings">;
  storage: KeyValueStorage;
  /** Whose settings (a shared device keeps each person's apart). */
  userId: string;
};

const CACHE_KEY = "bundle.navigation.";

export class NavigationViewModel extends ViewModel<NavigationState> {
  constructor(private readonly deps: NavigationDeps) {
    const raw = deps.storage.get(`${CACHE_KEY}${deps.userId}`);
    let cached: NavigationSettings | undefined;
    try {
      cached = raw === undefined ? undefined : parseNavigationSettings(JSON.parse(raw));
    } catch {
      cached = undefined;
    }
    super({ status: cached === undefined ? "loading" : "cached", settings: cached ?? {}, saving: false, saveFailed: false });
  }

  /** The bottom bar and Home's top row for a workspace's areas. */
  tabs(features: WorkspaceFeatures | undefined): PhoneTabs {
    return phoneTabs(features, this.state.settings);
  }

  load(): Promise<void> {
    return this.deps.api.navigationSettings().then(
      (settings) => {
        // A save started meanwhile wins; it brings the server's answer when it lands.
        if (this.state.saving) return;
        this.apply(settings, { status: "ready" });
      },
      () => {
        if (this.state.status === "loading") this.update({ status: "failed" });
      },
    );
  }

  /** Moves an area to `index` in my order. */
  move(area: PhoneArea, index: number): Promise<void> {
    return this.save(moveArea(this.state.settings, area, index));
  }

  /** Takes an area off the bar (Home's top row shows it) or puts it back. */
  setHidden(area: PhoneArea, hidden: boolean): Promise<void> {
    return this.save(setAreaHidden(this.state.settings, area, hidden));
  }

  /** Puts an area at `index` of the bar or Home's top row, as `features`' workspace shows them (Settings → Navigation). */
  place(features: WorkspaceFeatures | undefined, area: PhoneArea, section: PhoneSection, index: number): Promise<void> {
    return this.save(placeArea(this.state.settings, features, area, section, index));
  }

  /** Back to the app's defaults. */
  reset(): Promise<void> {
    return this.save({});
  }

  private async save(next: NavigationSettings): Promise<void> {
    const before = this.state.settings;
    this.apply(next, { saving: true, saveFailed: false });
    try {
      this.apply(await this.deps.api.updateNavigationSettings(next), { saving: false, status: "ready" });
    } catch {
      this.apply(before, { saving: false, saveFailed: true });
    }
  }

  private apply(settings: NavigationSettings, patch: Partial<NavigationState>): void {
    const clean = parseNavigationSettings(settings);
    this.deps.storage.set(`${CACHE_KEY}${this.deps.userId}`, JSON.stringify(clean));
    this.setState({ ...this.state, ...patch, settings: clean });
  }
}
